import styled from "styled-components";
import { InputWrapper } from "common/components/Wrapper/InputBase";
import { useValidCheckSingle } from "common/hooks/validCheck";
import { HiddenCheckbox } from "./FormInputs";
import { colors } from "common/const/colors";
import { textStyleButton2 } from "common/components";

const buttonSizes = {
  medium: {
    width: "11.125rem",
    height: "2.125rem",
    offset: "0.25",
  },
  large: {
    width: "17.125rem",
    height: "2.625rem",
    offset: "0.5",
  },
};

const ToggleWrapper = styled.div`
  position: relative;
  display: flex;
  width: ${({ size }) => buttonSizes[size].width};
  height: ${({ size }) => buttonSizes[size].height};
  background-color: ${({ $disabled }) =>
    $disabled ? colors.Gray : colors.White};
  border: 1px solid var(--Gray);
  border-radius: 999px;
  overflow: hidden;
  cursor: pointer;
`;

const ToggleSlider = styled.div`
  position: absolute;
  top: 0;
  left: ${({ $isLeft, size }) =>
    $isLeft ? 0 : `calc(50% - ${buttonSizes[size].offset}rem)`};
  width: ${({ size }) => `calc(50% + ${buttonSizes[size].offset}rem)`};
  height: 100%;
  background-color: ${({ $disabled }) =>
    $disabled ? colors.White : colors.MainBlue};
  border: 1px solid var(--White);
  border-radius: 999px;
  transition: left 0.3s ease;
  z-index: 0;
`;

const OptionButton = styled.div`
  flex: 1;
  ${textStyleButton2}
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ active, $disabled }) =>
    active !== $disabled ? colors.White : colors.LightNavy};
  z-index: 1;
  transition: color 0.3s ease;

  transform: ${({ $isLeft, size }) =>
    $isLeft
      ? `translateX(${buttonSizes[size].offset / 2}rem)`
      : `translateX(-${buttonSizes[size].offset / 2}rem)`};
`;

export const ToggleTextBase = ({
  // InputWrapper Props
  label,
  caption,
  error,
  required,

  // ToggleText Props
  size = "medium",
  disabled = false,
  options,

  value,
  onChange,
  inputProps,
}) => {
  const isLeft = value === options?.[0]?.value;

  return (
    <InputWrapper
      label={label}
      caption={caption}
      error={error}
      rules={{ required }}>
      <ToggleWrapper size={size} $disabled={disabled} onClick={onChange}>
        <HiddenCheckbox name={inputProps?.name} checked={value} readOnly />
        <ToggleSlider size={size} $disabled={disabled} $isLeft={isLeft} />
        {options.map((opt, index) => (
          <OptionButton
            size={size}
            key={opt.value}
            active={value === opt.value}
            $isLeft={index === 0}
            $disabled={disabled}>
            {opt.label}
          </OptionButton>
        ))}
      </ToggleWrapper>
    </InputWrapper>
  );
};

export const ToggleText = ({
  //InputWrapper
  label,
  caption,

  //ToggleText
  name,
  value,
  onChange,
  size = "medium",
  required = false,
  fixed = false,
  disabled = false,
  options,
}) => {
  const isControlled =
    typeof value === "string" && typeof onChange === "function";

  const { inputProps, error, onValueChange } = useValidCheckSingle({
    name,
    rules: { required },
    defaultValue: options?.[0]?.value,
  });

  const currentValue = isControlled ? value : inputProps.value;

  const handleToggle = () => {
    if (disabled || fixed) return;
    const [opt1, opt2] = options.map((o) => o.value);
    const newValue = currentValue === opt1 ? opt2 : opt1;

    isControlled ? onChange?.(newValue) : onValueChange(newValue);
  };

  return (
    <ToggleTextBase
      label={label}
      caption={caption}
      required={required}
      error={error}
      size={size}
      disabled={disabled}
      options={options}
      value={currentValue}
      onChange={handleToggle}
      inputProps={inputProps}
    />
  );
};
